import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import SwipeStack from "@/components/discovery/SwipeStack";
import DailySignalCard from "@/components/discovery/DailySignalCard";
import DiscoveryFiltersSheet from "@/components/discovery/DiscoveryFiltersSheet";
import { SwipeCardSkeleton } from "@/components/common/Skeletons";
import { useProfile } from "@/lib/profile/useProfile";
import { useDiscovery } from "@/lib/discovery/useDiscovery";
import { getApp } from "@/lib/apps";
import { trackEvent } from "@/lib/analytics/track";
import type { DiscoveryCandidate } from "@/lib/discovery/types";
import type { DiscoveryFilters } from "@/lib/discovery/filters";

const todayKey = () => new Date().toISOString().slice(0, 10);

const hashSeed = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const DiscoverInner = () => {
  const { profile, loading: pLoading } = useProfile();
  const app = profile?.active_app ?? null;
  const {
    candidates,
    rawCandidates,
    loading,
    act,
    reload,
    filters,
    setFilters,
    resetFilters,
    filterDelta,
  } = useDiscovery({ app });

  const meta = useMemo(() => (app ? getApp(app) : null), [app]);
  const [signalNonce, setSignalNonce] = useState(0);
  const [skippedIds, setSkippedIds] = useState<string[]>([]);
  const viewedRef = useRef<string | null>(null);

  // Deterministic-but-rotatable daily pick. Stable across renders for the
  // same (user, app, day, nonce) tuple; advances when user taps Refresh
  // (nonce++) or skips (id added to skippedIds).
  const daily: DiscoveryCandidate | undefined = useMemo(() => {
    if (!profile?.id || !app) return candidates[0];
    const pool = candidates.filter((c) => !skippedIds.includes(c.id));
    if (pool.length === 0) return undefined;
    const seed = hashSeed(`${profile.id}|${app}|${todayKey()}|${signalNonce}`);
    return pool[seed % pool.length];
  }, [candidates, profile?.id, app, signalNonce, skippedIds]);

  // Stack should not include the daily-signal candidate to avoid duplication.
  const stackCandidates = useMemo(
    () => (daily ? candidates.filter((c) => c.id !== daily.id) : candidates),
    [candidates, daily],
  );

  // One daily_signal_view per (id, day) — prevents refresh spam from inflating it.
  useEffect(() => {
    if (!daily || !app) return;
    const k = `${daily.id}|${todayKey()}`;
    if (viewedRef.current === k) return;
    viewedRef.current = k;
    trackEvent("daily_signal_view", { app, props: { candidate_id: daily.id } });
  }, [daily, app]);

  const handleRefreshSignal = useCallback(() => {
    setSignalNonce((n) => n + 1);
    trackEvent("daily_signal_refresh", { app, props: filterDelta });
  }, [app, filterDelta]);

  const handleSkipSignal = useCallback(
    (c: DiscoveryCandidate) => {
      setSkippedIds((prev) => (prev.includes(c.id) ? prev : [...prev, c.id]));
      trackEvent("daily_signal_skip", { app, props: { candidate_id: c.id } });
    },
    [app],
  );

  const handleOpenSignal = useCallback(
    async (c: DiscoveryCandidate) => {
      trackEvent("daily_signal_accept", { app, props: { candidate_id: c.id } });
      const result = await act(c.id, "like");
      if ("error" in result) {
        toast({
          title: "Couldn't send",
          description: result.error,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: result.matched ? "It's a match!" : "Signal sent",
        description: result.matched
          ? `You and ${c.display_name ?? "they"} matched — say hi.`
          : `${c.display_name ?? "They"} will see your signal.`,
      });
    },
    [act, app],
  );

  const handleApplyFilters = useCallback(
    (next: DiscoveryFilters) => {
      setFilters(next);
      trackEvent("discovery_filter_apply", {
        app,
        props: {
          max_km: next.maxKm,
          lane: next.lane,
          verified_only: next.verifiedOnly,
          with_voice_intro: next.withVoiceIntro,
          has_keyword: next.promptKeyword.trim().length > 0,
        },
      });
    },
    [app, setFilters],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
    trackEvent("discovery_filter_reset", { app });
  }, [app, resetFilters]);

  if (pLoading) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-8">
        <SwipeCardSkeleton />
      </main>
    );
  }

  if (!app) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-2xl font-semibold">Pick a community first</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Visit your account to choose your active app.
        </p>
        <Link
          to="/account"
          className="mt-6 inline-flex rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
        >
          Go to Account
        </Link>
      </main>
    );
  }

  return (
    <main className={`mx-auto max-w-2xl px-6 py-8 ${meta?.themeClass ?? ""}`}>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {meta?.community}
          </p>
          <h1 className="mt-1 text-3xl font-semibold">Discover</h1>
        </div>
        <div className="flex items-center gap-2">
          <DiscoveryFiltersSheet
            filters={filters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            resultCount={candidates.length}
            rawCount={rawCandidates.length}
          />
          <Link
            to="/matches"
            className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-accent transition"
          >
            Matches
          </Link>
        </div>
      </header>

      {daily && (
        <div className="mb-6">
          <DailySignalCard
            candidate={daily}
            onRefresh={handleRefreshSignal}
            onOpen={handleOpenSignal}
            onSkip={handleSkipSignal}
            refreshing={loading}
          />
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          Finding your matches…
        </div>
      ) : stackCandidates.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          {rawCandidates.length === 0
            ? "No candidates in your area yet — check back soon."
            : "Filters hid every candidate. Try widening them."}
        </div>
      ) : (
        <SwipeStack candidates={stackCandidates} onAction={act} onEmpty={reload} />
      )}
    </main>
  );
};

const Discover = () => (
  <OnboardingGate>
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <DiscoverInner />
      <SiteFooter />
    </div>
  </OnboardingGate>
);

export default Discover;
